import { ActivatedRouteSnapshot, DetachedRouteHandle, RouteReuseStrategy } from "@angular/router";

/**
 * Custom Route Reuse Strategy for tabbed interfaces
 * This helps prevent the "Cannot activate an already activated outlet" error by
 * preserving component instances across tab switches rather than recreating them
 */
export class CustomRouteReuseStrategy implements RouteReuseStrategy {
    private storedRoutes = new Map<string, DetachedRouteHandle>();

    /**
     * Determines if route should be reused
     * @param future The future route that's about to be activated
     * @param curr The current activated route
     * @returns true if the route should be reused
     */
    shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
        // Check if this is a tab route
        const isTabRoute = future.routeConfig?.path?.startsWith('mainTabs/tabs/');

        // Default Angular behavior for most routes
        const defaultReuse = future.routeConfig === curr.routeConfig;

        // For tab routes, we want more aggressive reuse to prevent the outlet issue
        if (isTabRoute) {
            return defaultReuse ||
                (future.routeConfig?.path?.includes('tabs') && curr.routeConfig?.path?.includes('tabs'));
        }

        return defaultReuse;
    }

    /**
     * Determines if the route should be detached
     * @param route The route to consider for detachment
     * @returns true if the route should be detached
     */
    shouldDetach(route: ActivatedRouteSnapshot): boolean {
        // Only store tab routes
        return route.routeConfig?.path?.startsWith('mainTabs/tabs/') || false;
    }

    /**
     * Stores the detached route
     * @param route The route to store
     * @param handle The detached route handle
     */
    store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle): void {
        if (route.routeConfig?.path) {
            this.storedRoutes.set(route.routeConfig.path, handle);
        }
    }

    /**
     * Determines if a route should be reattached
     * @param route The route to consider for reattachment
     * @returns true if the route should be reattached
     */
    shouldAttach(route: ActivatedRouteSnapshot): boolean {
        return !!route.routeConfig && !!this.storedRoutes.get(route.routeConfig.path || '');
    }

    /**
     * Retrieves the stored route
     * @param route The route to retrieve
     * @returns The stored DetachedRouteHandle or null
     */
    retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
        if (!route.routeConfig) return null;
        return this.storedRoutes.get(route.routeConfig.path || '') || null;
    }
}
